'use strict';
import { resolveAvatarConfig } from './config';
import AvatarWidget from './AvatarWidget';
/**
 * Lazy-chunk entry for the conversational avatar. Imported (as the webpack
 * "avatar" chunk) from `sitesearch360.ts` only when `avatarConfig.id` is set,
 * so it costs nothing when the avatar is not configured.
 *
 * @param context the plugin Context (DI hub)
 * @param core    the SiteSearch360Core instance (used for search integration)
 * @param avatarConfig the public avatar configuration
 * @param isZoovu whether this is the Zoovu build/flavour (for endpoint resolution)
 */
export default function renderAvatar(context, core, avatarConfig, isZoovu = false) {
    // The backend endpoint, projectId and default label translations all derive
    // from the plugin config / environment.
    const pc = context.pluginConfiguration;
    const resolved = resolveAvatarConfig(avatarConfig, {
        siteId: pc.siteId,
        language: pc.language,
        ecom: pc.ecom,
        ecomDev: pc.ecomDev,
        ecomStage: pc.ecomStage,
        avatarBaseUrl: pc.avatarBaseUrl,
        baseUrl: pc.baseUrl,
        isZoovu
    });
    if (!resolved) {
        // No id → nothing to render (defensive; the caller already guards on id).
        return;
    }
    // One avatar per plugin instance — keyed on the Context (not the window), so
    // multiple plugin instances can each have their own avatar.
    if (context.avatarWidget) {
        return;
    }
    // Use the same identifier the search uses to store its result set
    // (page-view id when present, else the session id) so the avatar's realtime
    // session reloads exactly the result set the plugin saved. Mirrors how the
    // Zoe summary resolves its result-set identifier.
    const sessionId = core.getResultSetIdentifier();
    injectStyles();
    try {
        const widget = new AvatarWidget(context, core, resolved, sessionId);
        // Stored on the context so core.destroy() tears it down with the instance.
        context.avatarWidget = widget;
        void widget.render();
    }
    catch (err) {
        console.error('[avatar] Failed to initialise avatar widget:', err);
    }
}
/** Inject the avatar stylesheet once (ships inside this chunk, not the core CSS). */
function injectStyles() {
    const STYLE_ID = 'ss360-avatar-css';
    if (document.getElementById(STYLE_ID)) {
        return;
    }
    // eslint-disable-next-line global-require
    const css = require('../../sass/siteSearch/avatar.scss');
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.type = 'text/css';
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
}
